import { AsyncPipe, LowerCasePipe, NgOptimizedImage } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ListboxModule } from 'primeng/listbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { findSchoolLocationByCode } from '../../utils';
import { SchoolLocationFilterService } from '../../services';
import { CourseStore } from '../../store';
import { GroupedSchoolLocation } from '../../types';
import { SelectableField, SchoolLocation } from '@lx/quote-tool/lite/shared/types';
import { SearcherComponent, ListBoxComponent } from '@lx/quote-tool/lite/shared/ui/components';

@Component({
  standalone: true,
  selector: 'lx-location-selector',
  templateUrl: './location-selector.component.html',
  styleUrls: ['./location-selector.component.scss'],
  imports: [
    RadioButtonModule,
    FormsModule,
    ButtonModule,
    NgOptimizedImage,
    LowerCasePipe,
    InputTextModule,
    ListboxModule,
    AsyncPipe,
    SearcherComponent,
    ListBoxComponent,
  ],
})
export class LocationSelectorComponent implements OnInit, OnChanges {
  @ViewChild(SearcherComponent) searcherComponent: SearcherComponent | undefined;

  /**
   * Flag that indicates if the component is visible or not
   */
  @Input() visible = false;
  @Input() groupedSchoolLocations: GroupedSchoolLocation[] | undefined;
  @Output() selectedSchoolLocationChanged = new EventEmitter<SelectableField<SchoolLocation>>();

  // Sizes for the flag images
  readonly flagImageWidth = 19;
  readonly flagImageHeight = 13;

  groupedSchoolLocations$: Observable<GroupedSchoolLocation[]> | undefined;

  selectedSchoolLocationCode = '';

  private readonly schoolLocationFilterService = inject(SchoolLocationFilterService);
  private readonly courseStore = inject(CourseStore);
  private readonly destroyRef = inject(DestroyRef);

  ngOnChanges({ groupedSchoolLocations }: SimpleChanges): void {
    if (groupedSchoolLocations?.currentValue && this.selectedSchoolLocationCode) {
      this.groupedSchoolLocations$ = of(groupedSchoolLocations.currentValue);

      // Check if the selected school location is in the list of available locations if not, emit undefined value to the parent component
      const preselectedSchoolLocation = this.findSelectedSchoolLocation(groupedSchoolLocations.currentValue);
      if (!preselectedSchoolLocation) {
        setTimeout(() =>
          this.selectedSchoolLocationChanged.emit({
            value: undefined,
          }),
        );
      }
    }
  }

  ngOnInit(): void {
    this.courseStore.selectedSchoolLocation$.subscribe((selectedSchoolLocation) => {
      // If there is a selected school location in store, set the selected school location
      this.selectedSchoolLocationCode = selectedSchoolLocation?.schoolCode || '';
    });
  }

  /**
   * Filter both the countries and the schools by the query
   */
  filterLocation(query: string) {
    if (query) {
      this.groupedSchoolLocations$ = this.schoolLocationFilterService.filterGroupedSchoolLocationsByQuery(this.groupedSchoolLocations$, query);
    } else {
      this.groupedSchoolLocations$ = of(this.groupedSchoolLocations);
    }
  }

  /**
   * Find the selected school location by the selected school location code and
   * emit the selectedSchoolLocationChanged event
   */
  triggerSelectedSchoolLocationChanged() {
    this.groupedSchoolLocations$
      .pipe(
        map((inSchoolLocations) => this.findSelectedSchoolLocation(inSchoolLocations)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((selectedSchoolLocation: SchoolLocation | undefined) => {
        this.selectedSchoolLocationChanged.emit({
          value: selectedSchoolLocation,
          fromUser: true,
        });
      });
  }

  /**
   * Find the selected school location by the selected school location code
   */
  private findSelectedSchoolLocation(inSchoolLocations: GroupedSchoolLocation[]): SchoolLocation | undefined {
    return findSchoolLocationByCode(inSchoolLocations, this.selectedSchoolLocationCode);
  }
}
